import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Tooltip,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { AlertTriangle, Boxes, Eye, RefreshCw, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: '16px',
  overflow: 'hidden',
}));

export default function RisorseApiPasspartout() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('nome_risorsa');
  const [totalItems, setTotalItems] = useState(0);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);

  const [search, setSearch] = useState('');
  const [nomeRisorsa, setNomeRisorsa] = useState('all');
  const [method, setMethod] = useState('all');
  const [nomeRisorsaOptions, setNomeRisorsaOptions] = useState([]);
  const [methodOptions, setMethodOptions] = useState([]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalItems / rowsPerPage)), [totalItems, rowsPerPage]);

  const renderMethodChip = (value) => {
    const methodLabel = String(value || '').toUpperCase();
    let color = 'default';
    switch (methodLabel) {
      case 'GET':
        color = 'success';
        break;
      case 'POST':
        color = 'info';
        break;
      case 'PUT':
      case 'PATCH':
        color = 'warning';
        break;
      case 'DELETE':
        color = 'error';
        break;
      default:
        color = 'default';
        break;
    }
    return <Chip label={methodLabel || '-'} color={color} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />;
  };

  const openView = (row) => {
    setViewRow(row || null);
    setViewOpen(true);
  };

  const closeView = () => {
    setViewOpen(false);
    setViewRow(null);
  };

  const openSyncConfirm = () => {
    if (syncing) return;
    setSyncConfirmOpen(true);
  };

  const closeSyncConfirm = () => {
    setSyncConfirmOpen(false);
  };

  const fetchFilters = async () => {
    try {
      const res = await api.get('/risorseapipasspartout/filters/options');
      const data = res.data || {};
      setNomeRisorsaOptions((data.nome_risorsa || []).filter(Boolean));
      setMethodOptions((data.method || []).filter(Boolean));
    } catch (error) {
      const msg = error?.response?.data?.message || 'Errore nel caricamento dei filtri Risorse API';
      toast.error(msg);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        orderBy,
        orderDirection: order,
      };
      if (search) params.search = search;
      if (nomeRisorsa !== 'all') params.nome_risorsa = nomeRisorsa;
      if (method !== 'all') params.method = method;

      const res = await api.get('/risorseapipasspartout', { params });
      const data = res.data || {};
      setRows(Array.isArray(data.items) ? data.items : []);
      setTotalItems(Number(data.pagination?.totalItems || 0));
    } catch (error) {
      const msg = error?.response?.data?.message || 'Errore nel caricamento delle Risorse API';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, order, orderBy, search, nomeRisorsa, method]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSync = async () => {
    let toastId = null;
    try {
      setSyncing(true);
      toastId = toast.loading('Sincronizzazione Risorse API in corso...');
      const res = await api.post('/risorseapipasspartout/sync');
      const data = res.data || {};
      toast.update(toastId, {
        render: `Sincronizzazione completata: nuovi ${data.inserted || 0}, aggiornati ${data.updated || 0}`,
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      await fetchFilters();
      await fetchData();
    } catch (error) {
      const msg = error?.response?.data?.message || 'Errore durante la sincronizzazione';
      if (toastId) {
        toast.update(toastId, { render: msg, type: 'error', isLoading: false, autoClose: 4500 });
      } else {
        toast.error(msg);
      }
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <StyledCard sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'rgba(37, 99, 235, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Boxes size={20} color="#2563EB" />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              Risorse API
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Elenco endpoint Passpartout (sola lettura)
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<RefreshCw size={16} />}
            onClick={openSyncConfirm}
            disabled={syncing}
            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 800 }}
          >
            {syncing ? 'Sincronizzazione...' : 'Sincronizza'}
          </Button>
        </Box>
      </StyledCard>

      <StyledCard>
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                placeholder="Cerca (nome, descrizione, method, url, regex)"
                variant="outlined"
                size="small"
                fullWidth
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={20} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Nome risorsa</InputLabel>
                <Select
                  value={nomeRisorsa}
                  label="Nome risorsa"
                  onChange={(e) => {
                    setNomeRisorsa(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="all">Tutte</MenuItem>
                  {nomeRisorsaOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Method</InputLabel>
                <Select
                  value={method}
                  label="Method"
                  onChange={(e) => {
                    setMethod(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="all">Tutti</MenuItem>
                  {methodOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Chip
                label={`Totale: ${totalItems} (${page + 1}/${totalPages})`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '10px', height: 24 }}
              />
            </Grid>
          </Grid>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'nome_risorsa'}
                      direction={orderBy === 'nome_risorsa' ? order : 'asc'}
                      onClick={() => handleRequestSort('nome_risorsa')}
                    >
                      Nome risorsa
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Descrizione</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'method'}
                      direction={orderBy === 'method' ? order : 'asc'}
                      onClick={() => handleRequestSort('method')}
                    >
                      Method
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'base_url'}
                      direction={orderBy === 'base_url' ? order : 'asc'}
                      onClick={() => handleRequestSort('base_url')}
                    >
                      Base URL
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Regex</TableCell>
                  <TableCell>Params</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'updated_at'}
                      direction={orderBy === 'updated_at' ? order : 'asc'}
                      onClick={() => handleRequestSort('updated_at')}
                    >
                      Agg.
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={26} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary', fontSize: '12px' }}>
                      Nessuna risorsa trovata
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{row.nome_risorsa}</TableCell>
                      <TableCell sx={{ minWidth: 220 }}>{row.descrizione || ''}</TableCell>
                      <TableCell sx={{ width: 90 }}>
                        {renderMethodChip(row.method)}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 520, whiteSpace: 'nowrap' }}>
                        <Tooltip title={row.base_url || ''}>
                          <span>{row.base_url || ''}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 520,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          lineHeight: 1.25,
                          fontFamily:
                            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                        }}
                      >
                        <Tooltip title={row.regex || ''}>
                          <span>{row.regex || ''}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ width: 70 }}>
                        {row.parameters ? (
                          <Tooltip
                            title={
                              <pre style={{ margin: 0, fontSize: 11 }}>
                                {(() => {
                                  try {
                                    return JSON.stringify(row.parameters, null, 2);
                                  } catch (e) {
                                    return String(row.parameters);
                                  }
                                })()}
                              </pre>
                            }
                          >
                            <Chip label="JSON" size="small" variant="outlined" />
                          </Tooltip>
                        ) : (
                          <Chip label="-" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell sx={{ width: 140 }}>
                        {row.updated_at ? new Date(row.updated_at).toLocaleString('it-IT') : ''}
                      </TableCell>
                      <TableCell align="right" sx={{ width: 72 }}>
                        <Tooltip title="Visualizza">
                          <IconButton size="small" onClick={() => openView(row)}>
                            <Eye size={18} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Righe per pagina"
          />
        </CardContent>
      </StyledCard>

      <Dialog open={viewOpen} onClose={closeView} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'rgba(37, 99, 235, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Boxes size={20} color="#2563EB" />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              Dettaglio endpoint
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Tutti i campi salvati a DB
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Nome risorsa
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {viewRow?.nome_risorsa || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Method
              </Typography>
              <Box sx={{ mt: 0.5 }}>{renderMethodChip(viewRow?.method)}</Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Descrizione
              </Typography>
              <Typography variant="body2">{viewRow?.descrizione || '-'}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Base URL
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                {viewRow?.base_url || '-'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Regex
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: 1.3,
                }}
              >
                {viewRow?.regex || '-'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Parameters
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  mt: 0.5,
                  p: 1.25,
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'rgba(145, 158, 171, 0.06)',
                  fontSize: 12,
                  overflow: 'auto',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                }}
              >
                {(() => {
                  if (!viewRow?.parameters) return '-';
                  try {
                    return JSON.stringify(viewRow.parameters, null, 2);
                  } catch (e) {
                    return String(viewRow.parameters);
                  }
                })()}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Versione
              </Typography>
              <Typography variant="body2">{viewRow?.versione || '-'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                ID (interno)
              </Typography>
              <Typography variant="body2">{viewRow?.id ?? '-'}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Creato il
              </Typography>
              <Typography variant="body2">
                {viewRow?.created_at ? new Date(viewRow.created_at).toLocaleString('it-IT') : '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Aggiornato il
              </Typography>
              <Typography variant="body2">
                {viewRow?.updated_at ? new Date(viewRow.updated_at).toLocaleString('it-IT') : '-'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Flags
              </Typography>
              <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                <Chip label={`next: ${Number(viewRow?.next) ? '1' : '0'}`} size="small" variant="outlined" />
                <Chip label={`filter: ${Number(viewRow?.filter) ? '1' : '0'}`} size="small" variant="outlined" />
                <Chip label={`coordinate: ${Number(viewRow?.coordinate) ? '1' : '0'}`} size="small" variant="outlined" />
                <Chip label={`max: ${Number(viewRow?.max) ? '1' : '0'}`} size="small" variant="outlined" />
                <Chip label={`location: ${Number(viewRow?.location) ? '1' : '0'}`} size="small" variant="outlined" />
                <Chip label={`fields: ${Number(viewRow?.fields) ? '1' : '0'}`} size="small" variant="outlined" />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeView}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={syncConfirmOpen} onClose={closeSyncConfirm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'rgba(239, 68, 68, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AlertTriangle size={18} color="#EF4444" />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              Conferma sincronizzazione Risorse API
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Aggiorna il catalogo endpoint da Passpartout
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
              Stai per avviare una sincronizzazione con Passpartout che scarica l’elenco completo degli endpoint disponibili e aggiorna i
              record locali (nuovi e modifiche).
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
              L’operazione può richiedere alcuni secondi e sovrascrive i campi tecnici (regex, parametri, flags, versione) con i valori più
              recenti pubblicati dal servizio.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeSyncConfirm} disabled={syncing} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              closeSyncConfirm();
              await handleSync();
            }}
            disabled={syncing}
            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 800 }}
          >
            Conferma
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
